import React, { useState, useEffect } from 'react';
import { DateObject, Calendar } from 'react-multi-date-picker';
import { API_URL } from "../../../Constant.js";

// --- Helper Functions ---

function buildMonthMatrix(year, monthIndex) {
  const matrix = [];
  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);
  const numDays = lastDay.getDate();
  const startDow = firstDay.getDay(); 
  let currentDate = 1 - startDow;
  for (let row = 0; row < 6; row++) {
    const week = [];
    for (let col = 0; col < 7; col++) {
      const day = new Date(year, monthIndex, currentDate);
      if (day.getMonth() === monthIndex && day.getDate() > 0 && day.getDate() <= numDays) {
        week.push(day);
      } else {
        week.push(null);
      }
      currentDate++;
    }
    matrix.push(week);
  }
  return matrix;
}

function formatMonthYear(year, monthIndex) {
  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];
  return `${months[monthIndex]} ${year}`;
}

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function convert24To12(time24) {
  if (!time24) return '';
  const parts = time24.split(':');
  let hour = parseInt(parts[0], 10);
  const minute = parts[1] || '00';
  const meridiem = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  if (hour === 0) hour = 12;
  const hh = hour < 10 ? '0' + hour : '' + hour;
  return `${hh}:${minute} ${meridiem}`;
}

const parse12TimeTo24 = (timeStr) => {
  if (!timeStr) return '';
  const [time, meridiem] = timeStr.split(' ');
  let [hour, minute] = time.split(':').map(Number);
  if (meridiem === 'PM' && hour < 12) hour += 12;
  if (meridiem === 'AM' && hour === 12) hour = 0;
  const hStr = hour < 10 ? '0' + hour : '' + hour;
  const mStr = minute < 10 ? '0' + minute : '' + minute;
  return `${hStr}:${mStr}`;
};

function isPastDate(day) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(day);
  d.setHours(0, 0, 0, 0);
  return d < today;
}

function isToday(day) {
  const today = new Date();
  return (
    day.getFullYear() === today.getFullYear() &&
    day.getMonth() === today.getMonth() &&
    day.getDate() === today.getDate()
  );
}

const generateTimeOptions = () => {
  const options = [];
  for (let meridiem of ['AM', 'PM']) {
    for (let h = 1; h <= 12; h++) {
      const hourStr = h < 10 ? '0' + h : '' + h;
      for (let m = 0; m < 60; m += 5) {
        const minuteStr = m < 10 ? '0' + m : '' + m;
        options.push(`${hourStr}:${minuteStr} ${meridiem}`);
      }
    }
  }
  return options;
};

const timeOptions = generateTimeOptions();

// --- Component: MonthlyCalendar ---
function MonthlyCalendar({ counselorId = 1 }) {
  const [displayYear, setDisplayYear] = useState(2025);
  const [displayMonthIndex, setDisplayMonthIndex] = useState(1);
  const [calendarSchedule, setCalendarSchedule] = useState([]);

  // State for modal popup editing availability of a particular date
  const [showEditModal, setShowEditModal] = useState(false);
  const [editDate, setEditDate] = useState('');
  const [editTimeSlots, setEditTimeSlots] = useState([{ fromTime: '', toTime: '' }]);

  useEffect(() => {
    fetchCalendarSchedule();
  }, [displayYear, displayMonthIndex]);

  const fetchCalendarSchedule = async () => {
    const monthYear = `${displayYear}-${(displayMonthIndex + 1).toString().padStart(2, '0')}`;
    try {
      const res = await fetch(
        `${API_URL}api/availability/calendar/${counselorId}?monthYear=${monthYear}`
      );
      if (res.ok) {
        const data = await res.json();
        setCalendarSchedule(data);
      }
    } catch (err) {
      console.error("Error fetching calendar schedule:", err);
    }
  };

  const nextMonth = () => {
    let newMonth = displayMonthIndex + 1;
    let newYear = displayYear;
    if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }
    setDisplayYear(newYear);
    setDisplayMonthIndex(newMonth);
  };

  const prevMonth = () => {
    let newMonth = displayMonthIndex - 1;
    let newYear = displayYear;
    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    }
    setDisplayYear(newYear);
    setDisplayMonthIndex(newMonth);
  };

  const monthMatrix = buildMonthMatrix(displayYear, displayMonthIndex);

  // Build dictionary from API response.
  // API returns objects with keys: "date" and "timeSlots" (each slot has "startTime" and "endTime")
  const scheduleDict = {};
  calendarSchedule.forEach(item => {
    if (item.date) {
      scheduleDict[item.date] = item.timeSlots;
    }
  });

  // --- Modal Handlers ---
  const handleCellClick = (day) => {
    if (!day) return;
    if (isPastDate(day)) return;
    const dateStr = formatLocalDate(day);
    setEditDate(dateStr);
    // Map API time slots keys to modal keys: startTime -> fromTime, endTime -> toTime
    if (scheduleDict[dateStr] && scheduleDict[dateStr].length > 0) {
      const mappedSlots = scheduleDict[dateStr].map(slot => ({
        fromTime: slot.startTime,
        toTime: slot.endTime
      }));
      setEditTimeSlots(mappedSlots);
    } else {
      setEditTimeSlots([{ fromTime: '', toTime: '' }]);
    }
    setShowEditModal(true);
  };

  const handleEditTimeSlotChange = (index, field, value) => {
    const updated = [...editTimeSlots];
    updated[index][field] = value;
    setEditTimeSlots(updated);
  };

  const addEditTimeSlotRow = () => {
    setEditTimeSlots(prev => [...prev, { fromTime: '', toTime: '' }]);
  };

  const removeEditTimeSlotRow = (index) => {
    const updated = editTimeSlots.filter((_, i) => i !== index);
    setEditTimeSlots(updated);
  };

  const saveEditedAvailability = async () => {
    const validTimeSlots = editTimeSlots.filter(slot => slot.fromTime && slot.toTime);
    if (validTimeSlots.length === 0) {
      alert("Please add at least one complete time slot.");
      return;
    }
    const payload = {
      counselorId: counselorId,
      OriginalDates: [editDate],
      Dates: [editDate],
      TimeSlots: validTimeSlots.map(slot => ({
        StartTime: parse12TimeTo24(slot.fromTime),
        EndTime: parse12TimeTo24(slot.toTime)
      }))
    };
    try {
      const res = await fetch(
        `${API_URL}api/availability/datewise/edit`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        }
      );
      if (res.ok) {
        alert("Availability updated successfully!");
        setShowEditModal(false);
        fetchCalendarSchedule();
      } else {
        alert("Failed to update availability.");
      }
    } catch (err) {
      console.error("Error updating availability:", err);
    }
  };

  // --- Styling Objects ---
  const headerStyle = {
    background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
    color: 'white',
    padding: '15px 20px',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };

  const navButtonStyle = {
    backgroundColor: 'white',
    color: '#1976d2',
    border: 'none',
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    cursor: 'pointer',
    fontSize: '1.2em',
    fontWeight: 'bold',
    boxShadow: '0px 2px 4px rgba(0,0,0,0.3)'
  };

  const gridContainerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '5px',
    marginTop: '15px'
  };

  const dayHeaderStyle = {
    textAlign: 'center',
    fontWeight: 'bold',
    padding: '8px 0',
    borderBottom: '2px solid #1976d2'
  };

  const cellStyle = {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '8px',
    minHeight: '90px',
    boxShadow: '0px 1px 3px rgba(0,0,0,0.1)',
    position: 'relative',
    cursor: 'pointer'
  };

  const cellHighlightStyle = {
    backgroundColor: 'lightblue',
    borderTop: '2px solid blue'
  };

  const todayStyle = {
    border: '2px solid #ff9800'
  };

  const slotStyle = {
    fontSize: '0.8em',
    marginBottom: '2px',
    backgroundColor: '#f5f5f5',
    padding: '2px 4px',
    borderRadius: '4px',
    marginTop: '3px'
  };

  // Modal styling
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const modalContentStyle = {
    background: '#fff',
    padding: '20px',
    borderRadius: '4px',
    width: '450px'
  };

  const modalHeaderStyle = {
    marginBottom: '20px',
    fontSize: '1.2em',
    fontWeight: 'bold'
  };

  const modalButtonStyle = {
    padding: '8px 12px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '10px'
  };

  // ------------------------------------------------

  return (
    <div style={{ padding: '20px', fontFamily: '"Roboto", sans-serif' }}>
      {/* Header */}
      <div style={headerStyle}>
        <button style={navButtonStyle} onClick={prevMonth}>‹</button>
        <h3 style={{ margin: 0 }}>{formatMonthYear(displayYear, displayMonthIndex)}</h3>
        <button style={navButtonStyle} onClick={nextMonth}>›</button>
      </div>

      {/* Weekday Headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', marginTop: '10px' }}>
        {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((day, idx) => (
          <div key={idx} style={dayHeaderStyle}>{day}</div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div style={gridContainerStyle}>
        {monthMatrix.map((week, rowIndex) =>
          week.map((day, colIndex) => {
            if (!day) {
              return <div key={`${rowIndex}-${colIndex}`} style={{ visibility: 'hidden' }} />;
            }
            const dateStr = formatLocalDate(day);
            const dayNum = day.getDate();
            const slots = isPastDate(day) ? [] : (scheduleDict[dateStr] || []);
            let combinedStyle = { ...cellStyle };
            if (!isPastDate(day) && slots.length > 0) {
              combinedStyle = { ...combinedStyle, ...cellHighlightStyle };
            }
            if (isToday(day)) {
              combinedStyle = { ...combinedStyle, ...todayStyle };
            }
            return (
              <div
                key={`${rowIndex}-${colIndex}`}
                style={combinedStyle}
                onClick={() => handleCellClick(day)}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{dayNum}</div>
                {slots.map((slot, i) => (
                  <div key={i} style={slotStyle}>
                    {slot.startTime} - {slot.endTime}
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>

      {/* Modal Popup for Editing Availability for a Single Date */}
      {showEditModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={modalHeaderStyle}>Set Datewise Availability for {editDate}</h3>
            <div style={{ marginBottom: '20px' }}>
              <Calendar
                multiple
                value={[new DateObject(editDate, "YYYY-MM-DD")]}
                onChange={(val) => {
                  if (Array.isArray(val) && val.length > 0) {
                    setEditDate(val[0].format("YYYY-MM-DD"));
                  }
                }}
                format="YYYY-MM-DD"
              />
            </div>
            {editTimeSlots.map((slot, index) => (
              <div key={index} style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                <label style={{ marginRight: '5px' }}>From:</label>
                <select
                  value={slot.fromTime}
                  onChange={(e) => handleEditTimeSlotChange(index, 'fromTime', e.target.value)}
                  style={{ marginRight: '10px' }}
                >
                  <option value="">Select Time</option>
                  {timeOptions.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <label style={{ marginRight: '5px' }}>To:</label>
                <select
                  value={slot.toTime}
                  onChange={(e) => handleEditTimeSlotChange(index, 'toTime', e.target.value)}
                  style={{ marginRight: '10px' }}
                >
                  <option value="">Select Time</option>
                  {timeOptions.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                {index === editTimeSlots.length - 1 && (
                  <button
                    onClick={addEditTimeSlotRow}
                    style={{ fontSize: '20px', lineHeight: '1', marginRight: '5px', cursor: 'pointer' }}
                    title="Add Time Slot"
                  >
                    +
                  </button>
                )}
                {editTimeSlots.length > 1 && (
                  <button
                    onClick={() => removeEditTimeSlotRow(index)}
                    style={{ fontSize: '20px', lineHeight: '1', cursor: 'pointer' }}
                    title="Remove This Time Slot"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button
                style={{ ...modalButtonStyle, backgroundColor: '#1976d2', color: 'white' }}
                onClick={saveEditedAvailability}
              >
                Save DateTime
              </button>
              <button
                style={{ ...modalButtonStyle, backgroundColor: '#e0e0e0' }}
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MonthlyCalendar;
